<template>
  <div
    :class="controlClasses"
    class="mds-form-control mds-input-phone-number"
    @mouseenter="showDisabledHint($event)"
    @mouseleave="hideDisabledHint()"
  >
    <label :for="uid" :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <div :style="innerStyles" class="inner">
      <div class="control-row">
        <div class="mds-form-control-icon" v-if="hasIconSlot">
          <!-- @slot Слот иконки -->
          <slot name="icon" />
        </div>

        <input
          :disabled="disabled"
          :id="uid"
          :ref="uid"
          :value="localValue"
          :placeholder="placeholder"
          type="tel"
          autocomplete="off"
          @input="onInput"
          @blur="onBlur"
          @focus="onFocus"
        />

        <button @click="onClear" class="clear" type="button" v-if="showClear">
          <VIcon material="cancel" />
        </button>
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="success" v-if="validation.success !== null">
          {{ validation.success }}
        </div>

        <div class="helper-text" v-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./VInputPhoneNumber.scss" />

<script lang="ts">
import VBaseControl from "@/core/services/VBaseControl";
import Constants from "@/core/misc/Constants";

export default defineComponent({
  name: 'VInputPhoneNumber',

  extends: VBaseControl,

  props: {
    // Базовые пропы (placeholder, disabled, disabledReasons, label, labelWidth,
    // layout, hideAsterisk, required, email, name, phone, min, max, minLength,
    // maxLength, validateMethod, warningMethod, successMethod) — из VBaseControl.

    /** v-model */
    modelValue: { type: String,  default: null },
    /** Кнопка очистки */
    clearable:  { type: Boolean, default: false },
    /** Выравнивание текста */
    textAlign: {
      type:      String,
      default:   null,
      validator: (value: string) => Constants.TEXT_ALIGNS.includes(value),
    },
  },

  emits: ['update:modelValue', 'blur', 'focus'],

  data() {
    return {
      // Локальное значение нужно, чтобы можно было держать каретку
      // и не дёргать modelValue на каждый ввод до синхронизации.
      localValue:     this.modelValue,
      lastLocalValue: null,
    };
  },

  computed: {
    element() {
      return this.$refs[this.uid];
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.clearable === true) classes.push("clearable");
      return classes;
    },

    showClear(): boolean {
      return this.clearable === true
        && this.localValue !== null
        && this.localValue !== ''
        && this.disabled === false;
    },

    /** labelStyles переопределяем — у инпута исторически width, а не flex. */
    labelStyles() {
      if (this.labelWidth === null) return null;
      return { width: this.labelWidth };
    },

    innerStyles() {
      const result: Record<string, string> = {};
      if (this.labelWidth !== null) result["width"] = `calc(100% - ${this.labelWidth})`;
      if (this.textAlign  !== null) result["text-align"] = this.textAlign;
      return result;
    },

    hasIconSlot(): boolean {
      return this.$slots.icon !== undefined;
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },
  },

  watch: {
    modelValue(newValue) {
      this.refresh(newValue);
      if (this.validation.isDirty === true) {
        this.validate();
      }
    },
  },

  methods: {
    onClear() {
      this.localValue     = null;
      this.lastLocalValue = null;
      this.$emit('update:modelValue', this.localValue);
      this.$emit('blur', this.localValue);
    },

    onInput(event) {
      if (event.isTrusted === false || this.element === null) return;

      const value          = event.target.value;
      const cursorPosition = this.element.selectionEnd;

      this.refresh(value, 'input');

      nextTick(() => {
        this.element.setSelectionRange(cursorPosition, cursorPosition);
      });
    },

    async onBlur() {
      if (this.localValue === null || this.localValue === '') {
        this.refresh(null, 'blur');
      } else {
        await this.validate();
        this.$emit('blur', this.localValue);
      }
    },

    onFocus(event) {
      this.validation.markTouched();
      this.refresh(this.localValue === null ? '' : this.localValue);
      this.$emit('focus', event);
    },

    refresh(newValue, eventName) {
      this.validation.markDirty();
      this.localValue = newValue;

      switch (eventName) {
        case 'blur':
          if (this.localValue !== this.lastLocalValue) {
            this.lastLocalValue = this.localValue;
            this.$emit('update:modelValue', this.localValue);
          }
          this.$emit('blur', this.localValue);
          break;
        case 'input':
          if (this.localValue !== this.lastLocalValue) {
            this.lastLocalValue = this.localValue;
            this.$emit('update:modelValue', this.localValue);
          }
          break;
      }
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      await this.validation.validate(this.localValue);
    },
  },
});
</script>
